<template>
    <div>
        <b-spinner v-if="!workflow" centered />

        <div :key="workflow.uuid" v-else>
            <template>
                <div class="wf-header">
                    <div class="wf-header-left">
                        <b-icon icon="share" class="wf-header-icon"></b-icon>
                        <h4 class="wf-title">{{ workflow.name }}</h4>
                    </div>
                    <div class="wf-header-actions" v-if="isDevEnv">
                        <b-button size="sm" variant="success" @click="save">
                            <b-icon icon="check2" class="mr-1"></b-icon>{{ $t('save') }}
                        </b-button>
                        <b-button size="sm" variant="outline-danger" @click="remove" v-if="canRemove">
                            <b-icon icon="trash" class="mr-1"></b-icon>{{ $t('remove') }}
                        </b-button>
                    </div>
                </div>

                <div class="wf-versions" v-if="isDevEnv">
                    <div class="wf-version-stage">
                        <span class="wf-version-label">IDE</span>
                        <b-form-select v-model="previewVersion" :options="availableVersions" size="sm" class="wf-version-select"></b-form-select>
                        <b-button size="sm" variant="link" class="wf-version-action" @click="forkVersionModal()" v-if="workflow.dev_version != ''">
                            <b-icon icon="bezier2"></b-icon>
                        </b-button>
                        <b-button size="sm" variant="link" class="text-danger wf-version-action" @click="removeVersion()" v-if="workflow.dev_version != '' && canRemoveVersion">
                            <b-icon icon="trash"></b-icon>
                        </b-button>
                    </div>
                    <div class="wf-version-promote">
                        <b-icon icon="chevron-right" class="wf-version-arrow"></b-icon>
                    </div>
                    <div class="wf-version-stage">
                        <span class="wf-version-label">Dev</span>
                        <b-form-select v-model="devVersion" @change="publishDevVersion($event)" :options="versionOptionsWithEmpty" size="sm" class="wf-version-select">
                            <template #first>
                                <b-form-select-option :value="null" disabled>{{ devVersion }}</b-form-select-option>
                            </template>
                        </b-form-select>
                        <span class="wf-version-chip" :class="workflow.dev_version ? 'wf-version-chip--success' : 'wf-version-chip--inactive'">
                            <b-icon :icon="workflow.dev_version ? 'check-circle-fill' : 'dash-circle'" class="wf-version-chip-icon"></b-icon>
                            {{ workflow.dev_version || $t('notActive') }}
                        </span>
                    </div>
                    <div class="wf-version-promote">
                        <b-icon icon="chevron-right" class="wf-version-arrow"></b-icon>
                    </div>
                    <div class="wf-version-stage">
                        <span class="wf-version-label">Live</span>
                        <b-form-select v-model="liveVersion" @change="publishLiveVersion($event)" :options="versionOptionsWithEmpty" size="sm" class="wf-version-select">
                            <template #first>
                                <b-form-select-option :value="null" disabled>{{ liveVersion }}</b-form-select-option>
                            </template>
                        </b-form-select>
                        <span class="wf-version-chip" :class="workflow.public_version ? 'wf-version-chip--live' : 'wf-version-chip--inactive'">
                            <b-icon :icon="workflow.public_version ? 'globe2' : 'dash-circle'" class="wf-version-chip-icon"></b-icon>
                            {{ workflow.public_version || $t('notPublished') }}
                        </span>
                    </div>
                </div>
                <div class="wf-versions wf-versions--readonly" v-else>
                    <span class="wf-version-chip wf-version-chip--live">
                        <b-icon icon="globe2" class="wf-version-chip-icon"></b-icon>
                        Live: {{ liveVersion }}
                    </span>
                </div>

                <Events :uuid="$route.params.id" v-model="settings.nodes" :eventNames="events" :key="settings.version" disableDryRun :readonly="!isDevEnv" />

                <main-dialog v-model="fork.modal" title="Fork version" @ok="forkVersion()" ok-title="Fork" ok-only>
                    <b-form-group :label="`${this.$t('fromVersion')}:`" :horizontal="true">
                        {{ fork.from }}
                    </b-form-group>

                    <b-form-group :label="`${this.$t('newVersion')}:`" :horizontal="true">
                        <b-input v-model="fork.to" />
                    </b-form-group>
                </main-dialog>

                <main-dialog
                    v-model="confirmDialog.show"
                    :title="confirmDialog.title"
                    :ok-title="confirmDialog.okTitle"
                    :ok-variant="confirmDialog.okVariant"
                    @ok="runConfirmAction"
                    @close="resetConfirmDialog"
                >
                    <p class="mb-0">{{ confirmDialog.message }}</p>
                </main-dialog>
            </template>
        </div>
    </div>
</template>

<script>
import Events from '../../components/base/Settings/Events'
import _ from 'lodash'
import VSelect from 'vue-select'
import platformEvents from './platform-events.json'

export default {
    name: 'Workflow',
    components: {
        Events,
        VSelect
    },

    data: function () {
        return {
            workflow: null,
            inspectVersion: null,
            fork: {
                modal: false,
                from: null,
                to: null
            },
            confirmDialog: {
                show: false,
                title: 'Confirm',
                message: '',
                okTitle: 'OK',
                okVariant: 'primary',
                action: null
            },
            settingsBar: false,

        }
    },

    computed: {
        isDevEnv() {
            return this.$store.getters.getCurrentEnv === 'dev'
        },

        availableVersions() {
            return this.workflow.settings.map(s => s.version)
        },

        versionOptionsWithEmpty() {
            return [
                { value: '', text: this.$t('deactivate') },
                ...this.availableVersions.map(v => ({ value: v, text: v }))
            ]
        },

        canRemoveVersion() {
            return this.workflow.settings.length > 1
        },

        previewVersion: {
            get() {
                return this.inspectVersion || this.settings.version
            },
            set(val) {
                this.reviewVersion(val)
            }
        },

        events() {
            let integrations = this.$store.getters.getIntegrations || []

            // Preserve integration metadata so workflow sidebar can show where each event comes from.
            let integrationEvents = _.flatten(integrations.map(i => {
                if (!i || !i.template || !Array.isArray(i.template.workflowevents)) {
                    return []
                }

                return i.template.workflowevents.map(event => {
                    let cloned = _.cloneDeep(event)
                    cloned._integration = {
                        integration_id: i.template.id,
                        integration_label: i.template.label,
                        settings: event.settings || [],
                        customSettingsView: event.customSettingsView || null
                    }
                    return cloned
                })
            }))

            let pEvents = _.cloneDeep(platformEvents)

            return integrationEvents.concat(pEvents)
        },

        currentVersion() {
            return this.inspectVersion || this.settings.version
        },

        settings() {
            const settings = _.get(this.workflow, 'settings', [])
            let version = this.workflow.dev_version
            if (this.$store.getters.isLiveEnv) {
                version = this.workflow.public_version
            }

            if (version == '') {
                return settings[settings.length - 1]
            }

            if (this.inspectVersion) {
                version = this.inspectVersion
            }

            return settings.find(s => {
                return s.version == version
            })
        },

        liveVersion: {
            get() {
                return this.workflow.public_version || this.$t('notPublished')
            },
            set(val) {
                // Handled by @change event
            }
        },

        devVersion: {
            get() {
                return this.workflow.dev_version || this.$t('notActive')
            },
            set(val) {
                // Handled by @change event
            }
        },

        canRemove() {
            return this.workflow.integrator == "platform"
        }
    },

    methods: {
        save() {
            const popup = this.$loadingPopup()
            return new Promise((resolve, reject) => {
                this.$workflowApi.patch("/workflow/" + this.$route.params.id, this.workflow).then(resp => {
                    resolve(resp)
                }).catch(err => {

                    this.$bvToast.toast(this.$t('unexpectedError'), {
                        variant: 'danger',
                        title: "Error"
                    })

                    console.error(err)
                    reject(err)
                }).finally(popup.hide)
            })
        },

        remove() {
            this.openConfirmDialog({
                message: `${this.$t('areYouSure')}?`,
                okVariant: 'danger',
                action: this.removeWorkflow
            })
        },

        removeWorkflow() {
            const loader = this.$loadingPopup()

            this.$workflowApi.delete("/workflow/" + this.$route.params.id).then(resp => {
                this.$router.push("/admin/dashboard")
                this.$store.commit("DELETE_WORKFLOW", this.$route.params.id)
            }).catch(err => {
                console.error(err)

                if (err.response) {
                    if (err.response.status == 409) {
                        this.$bvToast.toast(this.$t("unableToRemoveWorkflow"), {
                            variant: 'danger',
                            title: this.$t('error'),
                            autoHideDelay: 10000,
                            appendToast: false
                        })

                        return
                    }
                }

                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger',
                    title: "Error"
                })
            }).finally(() => {
                loader.hide()
            })
        },

        reviewVersion(version) {
            this.inspectVersion = version
        },

        publishDevVersion(v) {
            let message = "You are about to deploy " + v + " to dev environment. Are you sure?"
            if (v == '') {
                message = "You are about to deactivate workflow from dev environment, this action will also unpublish app if it is live. Are you sure?"
            }

            this.openConfirmDialog({
                message,
                okTitle: 'Confirm',
                okVariant: v == '' ? 'danger' : 'primary',
                action: () => {
                    this.workflow.dev_version = v
                    if (v == '') {
                        this.workflow.public_version = v
                    }

                    this.save()
                }
            })
        },

        publishLiveVersion(v) {
            let message = "You are about to deploy " + v + " to live environment. Are you sure?"
            if (v == '') {
                message = "You are about to unpublish workflow from live environment. Are you sure?"
            }

            this.openConfirmDialog({
                message,
                okTitle: 'Confirm',
                okVariant: v == '' ? 'danger' : 'primary',
                action: () => {
                    this.workflow.public_version = v
                    this.save()
                }
            })
        },

        removeVersion() {
            if (this.devVersion == this.currentVersion || this.liveVersion == this.currentVersion) {

                this.$bvToast.toast(this.$t('workflow.cantRemovePublishedVersion'), {
                    variant: 'danger',
                    title: "Error"
                })

                return
            }

            this.openConfirmDialog({
                message: "You are about to remove version " + this.currentVersion + ". Are you sure?",
                okTitle: this.$t('remove'),
                okVariant: 'danger',
                action: () => {
                    if (this.workflow.settings.length < 2) {

                        this.$bvToast.toast(this.$t('workflow.cantRemoveLastVersion'), {
                            variant: 'danger',
                            title: "Error"
                        })

                        return
                    }

                    this.workflow.settings = this.workflow.settings.filter(s => {
                        return s.version != this.currentVersion
                    })

                    this.save().then(() => {
                        this.inspectVersion = this.devVersion
                    })
                }
            })

        },

        openConfirmDialog({ message, title = 'Confirm', okTitle = 'OK', okVariant = 'primary', action }) {
            this.confirmDialog = {
                show: true,
                title,
                message,
                okTitle,
                okVariant,
                action
            }
        },

        runConfirmAction() {
            const action = this.confirmDialog.action
            if (typeof action === 'function') {
                action()
            }
        },

        resetConfirmDialog() {
            this.confirmDialog = {
                show: false,
                title: 'Confirm',
                message: '',
                okTitle: 'OK',
                okVariant: 'primary',
                action: null
            }
        },

        forkVersionModal() {
            this.fork.modal = true
            this.fork.from = this.inspectVersion || this.settings.version
        },

        forkVersion() {
            let fork = this.workflow.settings.find(s => {
                return s.version == this.fork.from
            })

            let newFork = _.cloneDeep(fork)
            newFork.version = this.fork.to
            this.workflow.settings.push(newFork)

            this.save().then(() => {
                this.inspectVersion = this.fork.to
            })
        }
    },

    beforeDestroy() {
        if (window.kortex) {
            window.kortex.unregisterTool('get_workflow_info')
            window.kortex.unregisterTool('list_workflows')
            window.kortex.unregisterTool('create_workflow')
        }
    },

    mounted() {
        this.$workflowApi.get("/workflow/" + this.$route.params.id).then(resp => {
            this.workflow = resp.data
        }).catch(err => {
            console.error(err)
            
            this.$bvToast.toast(this.$t('unexpectedError'), {
                variant: 'danger',
                title: "Error"
            })
        })

        this.$store.dispatch('fetchRoles').then(roles => {
            this.roles = roles
        })

        if (window.kortex) {
            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'get_workflow_info',
                        description: 'Returns metadata about the currently open workflow: UUID, name, dev/live version, and node count.',
                        parameters: { type: 'object', properties: {} }
                    }
                },
                () => {
                    if (!this.workflow) return { error: 'Workflow not loaded yet' }
                    return {
                        uuid: this.workflow.uuid,
                        name: this.workflow.name,
                        integrator: this.workflow.integrator,
                        dev_version: this.workflow.dev_version,
                        live_version: this.workflow.public_version,
                        versions: (this.workflow.settings || []).map(s => s.version),
                        current_nodes_count: this.settings ? (this.settings.nodes || []).length : 0
                    }
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'list_workflows',
                        description: 'Lists all workflows in the current project with their UUIDs, names, and deployment status.',
                        parameters: { type: 'object', properties: {} }
                    }
                },
                () => {
                    return this.$workflowApi.get('/workflows').then(resp => ({
                        workflows: (resp.data || []).map(w => ({
                            uuid: w.uuid,
                            name: w.name,
                            dev_version: w.dev_version,
                            live_version: w.public_version
                        }))
                    })).catch(err => ({ error: err.message }))
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'create_workflow',
                        description: 'Creates a new empty workflow with the given name and navigates to its editor.',
                        parameters: {
                            type: 'object',
                            required: ['name'],
                            properties: {
                                name: { type: 'string', description: 'Name for the new workflow' }
                            }
                        }
                    }
                },
                (args) => {
                    return this.$workflowApi.post('/workflow', { name: args.name }).then(resp => {
                        var uuid = resp.data.uuid
                        this.$router.push('/admin/workflow/' + uuid)
                        return { created: true, uuid: uuid, name: args.name }
                    }).catch(err => ({ error: err.message }))
                }
            )
        }
    }
}
</script>

<style scoped>
.workflow-version-dropdown ::v-deep .dropdown-toggle {
    color: var(--text-secondary) !important;
}

.workflow-version-dropdown ::v-deep .dropdown-menu {
    z-index: 1000 !important;
}

/* ── Header ─────────────────────────────────────── */
.wf-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0 14px;
    border-bottom: 1px solid var(--border-default);
    gap: 12px;
    flex-wrap: wrap;
}

.wf-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.wf-header-icon {
    color: var(--text-muted);
    font-size: 18px;
    flex-shrink: 0;
}

.wf-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.wf-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

/* ── Version Pipeline ───────────────────────────── */
.wf-versions {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-default);
    margin-bottom: 16px;
    overflow-x: auto;
    scrollbar-width: none;
}
.wf-versions::-webkit-scrollbar {
    display: none;
}

.wf-versions--readonly {
    padding: 12px 0;
}

.wf-version-stage {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.wf-version-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-muted, #949ba4);
    white-space: nowrap;
}

.wf-version-select {
    width: auto;
    min-width: 72px;
    max-width: 110px;
    font-size: 12px;
    padding: 2px 24px 2px 8px;
    height: 28px;
}

.wf-version-action {
    padding: 2px 4px;
    color: var(--text-secondary);
    line-height: 1;
}

.wf-version-promote {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.wf-version-arrow {
    color: var(--text-muted, #949ba4);
    font-size: 11px;
    opacity: 0.6;
}

/* ── Version Chips ──────────────────────────────── */
.wf-version-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.3;
}

.wf-version-chip-icon {
    font-size: 14px;
}

.wf-version-chip--success {
    background: rgba(72, 187, 120, 0.12);
    color: var(--success-start, #48bb78);
    border: 1px solid rgba(72, 187, 120, 0.25);
}

.wf-version-chip--live {
    background: rgba(108, 128, 255, 0.12);
    color: var(--primary-start, #6c80ff);
    border: 1px solid rgba(108, 128, 255, 0.25);
}

.wf-version-chip--inactive {
    background: var(--bg-card-secondary, rgba(255, 255, 255, 0.05));
    color: var(--text-muted, #949ba4);
    border: 1px solid var(--border-default);
}
</style>
